import { createUseStyles } from 'react-jss';
import { opacify } from '@picsart/cascade';

import { transformAngle } from './utils';
import { ICropSides } from './types';

const getResizeCursorDirection = (rotation: number, order: number) => {
  const angleInCursorTypeSystem = rotation + 22.5;
  return ['nwse-resize', 'ns-resize', 'nesw-resize', 'ew-resize'][(Math.floor(transformAngle(angleInCursorTypeSystem) / 45) + order) % 4];
};

interface IStyleProps {
  border: ICropSides;
  rotation: number;
  objectWidth: number;
  objectHeight: number;
  currentWidth: number;
  currentHeight: number;
  fullOverlay?: boolean;
}

const useStyles = createUseStyles({
  container: ({ border, rotation, objectWidth, objectHeight }: IStyleProps) => ({
    width: objectWidth,
    height: objectHeight,
    boxSizing: 'border-box',
    borderTop: `${border.top}px solid ${opacify('var(--backgrounds-absolute-dark)', 30)}`,
    borderLeft: `${border.left}px solid ${opacify('var(--backgrounds-absolute-dark)', 30)}`,
    borderRight: `${border.right}px solid ${opacify('var(--backgrounds-absolute-dark)', 30)}`,
    borderBottom: `${border.bottom}px solid ${opacify('var(--backgrounds-absolute-dark)', 30)}`,
    transform: `rotate(${rotation}deg)`,
  }),
  currentBox: ({ currentWidth, currentHeight }: IStyleProps) => ({
    width: currentWidth,
    height: currentHeight,
    position: 'relative',
  }),
  boxWrap: ({ fullOverlay }: IStyleProps) => ({
    '--backgrounds-absolute-light': '#fff',
    '--backgrounds-absolute-dark': '#000',
    '--border-color': 'var(--backgrounds-absolute-light)',
    '--border-width': '3px',
    '--border-seperator-width': '2px',
    '--item-count': 3,
    position: 'relative',
    display: 'grid',
    gridTemplateColumns: 'repeat(var(--item-count), 1fr)',
    gridTemplateRows: 'repeat(var(--item-count), 1fr)',
    border: 'var(--border-width) solid var(--border-color)',
    ...(fullOverlay && { backgroundColor: opacify('var(--backgrounds-absolute-dark)', 30) }),
  }),
  box: {
    '--border': 'calc(var(--border-width) / 2) solid var(--border-color)',
    '&:nth-child(3n+2)': {
      borderLeft: 'var(--border)',
      borderRight: 'var(--border)',
    },
    '&:nth-child(4)': {
      borderTop: 'var(--border)',
      borderBottom: 'var(--border)',
    },
    '&:nth-child(5)': {
      borderTop: 'var(--border)',
      borderBottom: 'var(--border)',
    },
    '&:nth-child(6)': {
      borderTop: 'var(--border)',
      borderBottom: 'var(--border)',
    },
    '&:not(:nth-child(3)):not(:nth-child(6)):not(:nth-child(9))': {
      boxShadow: `4px 0 6px ${opacify('var(--backgrounds-absolute-dark)', 30)}`,
    },
    '&:nth-child(n + 4)': {
      position: 'relative',
      '&::after': {
        content: '""',
        position: 'absolute',
        top: 0,
        right: 0,
        bottom: 0,
        left: 0,
        boxShadow: `0 -2px 6px ${opacify('var(--backgrounds-absolute-dark)', 30)}`,
      },
    },
  },
  option: {
    position: 'absolute',
    borderRadius: 4,
    '--width': '30px',
    '--height': '8px',
    '--options-position': 'calc((var(--border-width) / 2 * -1 - var(--height) / 2))',
    '&::after': {
      borderRadius: 4,
      transition: 'transform 0.1s',
    },
    '&::before': {
      content: "''",
      position: 'absolute',
      top: '-10px',
      left: '-10px',
      right: '-10px',
      bottom: '-10px',
      background: 'transparent',
    },
  },
  optionCorner: {
    width: 'var(--width)',
    height: 'var(--height)',
    background: 'var(--backgrounds-absolute-light)',
    '&::after': {
      content: "''",
      display: 'block',
      background: 'inherit',
      height: 'inherit',
    },
  },
  optionLeftTop: {
    left: 'var(--options-position)',
    top: 'var(--options-position)',
    cursor: ({ rotation }: { rotation: number }) => getResizeCursorDirection(rotation, 0),
    '&::after': {
      width: 'var(--height)',
      height: 'var(--width)',
      position: 'absolute',
      top: 0,
      left: 0,
      borderTopLeftRadius: 'initial',
    },
  },
  optionRightTop: {
    right: 'var(--options-position)',
    top: 'var(--options-position)',
    cursor: ({ rotation }: { rotation: number }) => getResizeCursorDirection(rotation, 2),
    '&::after': {
      width: 'var(--height)',
      height: 'var(--width)',
      position: 'absolute',
      right: 0,
      top: 0,
      borderTopRightRadius: 'initial',
    },
  },
  optionRightBottom: {
    right: 'var(--options-position)',
    bottom: 'var(--options-position)',
    cursor: ({ rotation }: { rotation: number }) => getResizeCursorDirection(rotation, 4),
    '&::after': {
      width: 'var(--height)',
      height: 'var(--width)',
      position: 'absolute',
      bottom: 0,
      right: 0,
      borderBottomRightRadius: 'initial',
    },
  },
  optionLeftBottom: {
    left: 'var(--options-position)',
    bottom: 'var(--options-position)',
    cursor: ({ rotation }: { rotation: number }) => getResizeCursorDirection(rotation, 6),
    '&::after': {
      width: 'var(--height)',
      height: 'var(--width)',
      position: 'absolute',
      bottom: 0,
      left: 0,
      borderBottomLeftRadius: 'initial',
    },
  },
  optionMiddle: {
    width: 'var(--width)',
    height: 'var(--height)',
    background: 'var(--backgrounds-absolute-light)',
    borderRadius: 4,
  },
  optionMiddleTop: {
    left: '50%',
    top: 'var(--options-position)',
    transform: 'translateX(-50%)',
    cursor: ({ rotation }: { rotation: number }) => getResizeCursorDirection(rotation, 1),
  },
  optionMiddleRight: {
    top: '50%',
    right: 'var(--options-position)',
    width: 'var(--height)',
    height: 'var(--width)',
    transform: 'translateY(-50%)',
    cursor: ({ rotation }: { rotation: number }) => getResizeCursorDirection(rotation, 3),
  },
  optionMiddleBottom: {
    left: '50%',
    bottom: 'var(--options-position)',
    transform: 'translateX(-50%)',
    cursor: ({ rotation }: { rotation: number }) => getResizeCursorDirection(rotation, 5),
  },
  optionMiddleLeft: {
    top: '50%',
    left: 'var(--options-position)',
    width: 'var(--height)',
    height: 'var(--width)',
    transform: 'translateY(-50%)',
    cursor: ({ rotation }: { rotation: number }) => getResizeCursorDirection(rotation, 7),
  },
});

export default useStyles;
